import { ExtendTimeModal, TimeExtensionHistoryModal } from '@exam-center/exam-event-time-extension';
import { TimePostponementModal } from '@exam-center/exam-event-time-postponement';
import { createContext, useCallback, useMemo, useState } from 'react';

import { PrintExaminationCodeLoadingModal } from '../../components';
import ConfirmPrintExaminationCodeModal from '../../components/confirm-print-examination-code-modal/confirm-print-examination-code-modal';
import { AddProctorModal, CreateThirdProctorModal } from '../../containers';

import type {
  AddProctorModalArgs,
  ConfirmPrintExaminationCodeArgs,
  ModalData,
  ProctoringDetailsModalAction,
  ProctoringDetailsModalContextValue,
  ProctoringDetailsModalProviderProps,
  SessionModalArgs,
  SetActionArgs,
} from './types';

const ProctoringDetailsModalContext = createContext<ProctoringDetailsModalContextValue | undefined>(
  undefined,
);

export default function ProctoringDetailsModalProvider(
  props: Readonly<ProctoringDetailsModalProviderProps>,
) {
  const { children } = props;
  const [modalData, setModalData] = useState<ModalData | null>(null);
  const [action, setAction] = useState<ProctoringDetailsModalAction | null>(null);
  const handleSetAction = useCallback(
    (args: SetActionArgs) => {
      const { action, data } = args;
      setAction(action);
      setModalData(data ?? null);
    },
    [setAction],
  );

  const handleClose = useCallback(() => {
    setAction(null);
    setModalData(null);
  }, []);

  const contextValue: ProctoringDetailsModalContextValue = useMemo(() => {
    return {
      setAction: handleSetAction,
      close: handleClose,
    };
  }, [handleSetAction, handleClose]);

  return (
    <ProctoringDetailsModalContext.Provider value={contextValue}>
      {children}
      <PrintExaminationCodeLoadingModal open={action === 'print-examination-code-loading'} />
      <ConfirmPrintExaminationCodeModal
        open={action === 'confirm-print-examination-code'}
        onClose={handleClose}
        {...(modalData as ConfirmPrintExaminationCodeArgs)}
      />
      <AddProctorModal
        isOpen={action === 'add-proctor'}
        onClose={handleClose}
        {...(modalData as AddProctorModalArgs)}
      />
      <CreateThirdProctorModal isOpen={action === 'create-proctor-third'} onClose={handleClose} />
      <ExtendTimeModal
        isOpen={action === 'extend-time'}
        onClose={handleClose}
        {...(modalData as SessionModalArgs)}
      />
      <TimeExtensionHistoryModal
        isOpen={action === 'time-extension-history'}
        onClose={handleClose}
        {...(modalData as SessionModalArgs)}
      />
      <TimePostponementModal isOpen={action === 'postpone-time'} onClose={handleClose} />
    </ProctoringDetailsModalContext.Provider>
  );
}

export { ProctoringDetailsModalContext };
